/**
 * TemplateTableRow Component
 * Table row for a single template
 */

import { memo } from 'react';
import { MoreHorizontal, Copy, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TableCell, TableRow } from '@/components/ui/table';
import { TEMPLATE_CATEGORIES, TEMPLATE_CHANNELS } from '@/constants/templateConstants';
import type { ITemplateTableRowProps } from '@/@types/messageTemplates';

// Format relative time - moved outside component to avoid impure function call during render
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

function TemplateTableRow({ template, isOwner, onEdit, onDelete, onCopy }: ITemplateTableRowProps) {
  const categoryLabel =
    TEMPLATE_CATEGORIES.find(c => c.value === template.category)?.label || template.category;

  // Truncate content for preview
  const previewContent =
    template.content.length > 80 ? template.content.slice(0, 80) + '...' : template.content;

  return (
    <TableRow className="hover:bg-muted/50">
      {/* Name & Preview */}
      <TableCell className="max-w-[300px]">
        <div>
          <p className="font-medium text-foreground truncate">{template.name}</p>
          <p className="text-sm text-muted-foreground truncate">{previewContent}</p>
        </div>
      </TableCell>

      {/* Category */}
      <TableCell>
        <Badge variant="secondary" className="bg-primary/10 text-primary border-0 text-xs">
          {categoryLabel}
        </Badge>
      </TableCell>

      {/* Channels */}
      <TableCell>
        <div className="flex gap-1 flex-wrap">
          {template.channels.map(channel => {
            const channelLabel = TEMPLATE_CHANNELS.find(c => c.value === channel)?.label || channel;
            return (
              <span
                key={channel}
                className="px-1.5 py-0.5 bg-muted rounded text-[10px] uppercase text-muted-foreground"
              >
                {channelLabel.slice(0, 3)}
              </span>
            );
          })}
        </div>
      </TableCell>

      {/* Usage */}
      <TableCell className="text-muted-foreground">{template.usageCount}</TableCell>

      {/* Last Updated */}
      <TableCell className="text-muted-foreground">
        {formatRelativeTime(template.updatedAt)}
      </TableCell>

      {/* Actions */}
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isOwner ? (
              <>
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onCopy}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem onClick={onCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Use Template
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

export const TemplateTableRowMemo = memo(TemplateTableRow);
